import React from 'react';
import { useTheme, type ThemePreference } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { THEME_PREFERENCE_OPTIONS } from './themePreferenceOptions';

export interface ThemePreferenceToggleProps {
  /** Icon-only on narrow layouts (topbar mobile) */
  compact?: boolean;
  /** Show text labels even when compact (preferences page) */
  showLabels?: boolean;
  className?: string;
  /** Controlled value (e.g. preferences form before save) */
  value?: ThemePreference;
  onChange?: (preference: ThemePreference) => void;
}

const ThemePreferenceToggle: React.FC<ThemePreferenceToggleProps> = ({
  compact = false,
  showLabels = false,
  className,
  value,
  onChange,
}) => {
  const { themePreference, setTheme, colors } = useTheme();
  const selected = value ?? themePreference;

  const segmentClass = (active: boolean) =>
    cn(
      'inline-flex items-center justify-center gap-1 h-7 min-w-[2rem] px-2 rounded-md text-xs font-medium transition-colors',
      active
        ? 'bg-indigo-600 text-white shadow-sm'
        : cn(colors.textMuted, colors.interactiveHover, 'hover:text-indigo-600 dark:hover:text-indigo-400'),
    );

  const handleSelect = (next: ThemePreference) => {
    if (onChange) {
      onChange(next);
    } else {
      setTheme(next);
    }
  };

  return (
    <div
      role="group"
      aria-label="Theme"
      className={cn(
        'inline-flex h-8 flex-shrink-0 items-center rounded-lg border p-0.5',
        colors.border,
        colors.surfaceHover,
        className,
      )}
    >
      {THEME_PREFERENCE_OPTIONS.map((option) => {
        const active = selected === option.value;
        const labelVisible = showLabels || !compact;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option.value)}
            className={segmentClass(active)}
            aria-pressed={active}
            title={option.title}
          >
            {option.icon}
            {labelVisible && (
              <span className={cn(compact && !showLabels ? 'hidden md:inline' : 'inline')}>
                {compact && !showLabels ? option.shortLabel : option.label}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ThemePreferenceToggle;
